/**
 * React Audio DAW - React Hooks and Components for Audio DAW
 *
 * Provides React bindings for the audio-daw.js library with:
 * - useAudioDAW hook for imperative API access
 * - State management integration
 * - Automatic cleanup on unmount
 *
 * @requires react ^16.8.0 || ^17.0.0 || ^18.0.0
 * @requires audio-daw.js (AudioDAW class must be available)
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import AudioDAW from './index.js';

/**
 * Custom hook for integrating AudioDAW with React
 * Manages DAW instance lifecycle and prevents re-initialization on re-renders
 *
 * @param {React.RefObject} canvasRef - Ref to canvas element for main timeline
 * @param {Object} options - AudioDAW configuration options
 * @param {React.RefObject} options.minimapCanvasRef - Optional ref to minimap canvas
 * @param {number} options.pixelsPerSecond - Initial zoom level (default: 50)
 * @param {boolean} options.followPlayhead - Auto-scroll during playback (default: true)
 * @param {boolean} options.enableLogging - Enable console logging (default: false)
 * @param {Function} options.logFunction - Custom logging function
 * @param {Object} options.theme - Custom theme object
 * @param {Function} options.onLoadingStateChange - Callback for track loading state changes
 * @param {Function} options.onPlayStateChange - Callback for playback state changes
 * @param {Function} options.onTimeUpdate - Callback for playhead position updates
 * @param {Function} options.onSelectionChange - Callback for selection changes
 * @param {Function} options.onRegionsChange - Callback for region changes
 * @param {Function} options.onTrackAdded - Callback when track is added
 * @param {Function} options.onTrackRemoved - Callback when track is removed
 * @param {Function} options.onTrackOffsetChange - Callback when track position changes
 * @param {Function} options.onTrackVolumeChange - Callback when track volume changes
 * @param {Function} options.onTrackPanChange - Callback when track pan changes
 * @param {Function} options.onTrackGainChange - Callback when track gain changes
 * @param {Function} options.onTrackCompressorChange - Callback when track compressor changes
 * @param {Function} options.onTrackColorChange - Callback when track color changes
 * @param {Function} options.onZoomChange - Callback when zoom level changes
 * @param {Function} options.onDurationChange - Callback when timeline duration changes
 * @param {Function} options.onScrollChange - Callback when scroll position changes
 * @param {Function} options.onActiveRegionGroupChange - Callback when active region group changes
 * @param {Function} options.onRegionGroupsChange - Callback when region groups change
 * @returns {AudioDAW|null} DAW instance or null if not yet initialized
 *
 * @example
 * function MyComponent() {
 *   const canvasRef = useRef();
 *   const minimapRef = useRef();
 *   const daw = useAudioDAW(canvasRef, {
 *     minimapCanvasRef: minimapRef,
 *     pixelsPerSecond: 100,
 *     onTrackAdded: (id) => console.log('Track added:', id)
 *   });
 *
 *   const handleAddTrack = async (file) => {
 *     if (daw) {
 *       await daw.addTrack(file, { fileName: file.name });
 *     }
 *   };
 *
 *   return (
 *     <div>
 *       <canvas ref={canvasRef} />
 *       <canvas ref={minimapRef} />
 *     </div>
 *   );
 * }
 */
export function useAudioDAW(canvasRef, options = {}) {
  const dawRef = useRef(null);
  const optionsRef = useRef(options);

  // Update options ref without triggering re-initialization
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  useEffect(() => {
    // Only initialize if canvas is available and DAW hasn't been created yet
    if (canvasRef.current && !dawRef.current) {
      const dawOptions = {
        ...optionsRef.current,
      };

      // Handle minimap canvas ref
      if (optionsRef.current.minimapCanvasRef?.current) {
        dawOptions.minimapCanvas = optionsRef.current.minimapCanvasRef.current;
      }

      // Remove the ref from options since it's already processed
      delete dawOptions.minimapCanvasRef;

      dawRef.current = new AudioDAW(canvasRef.current, dawOptions);
    }

    // Cleanup on unmount
    return () => {
      if (dawRef.current) {
        dawRef.current.destroy();
        dawRef.current = null;
      }
    };
  }, [canvasRef]);

  return dawRef.current;
}

/**
 * Hook for managing DAW state with React state synchronization
 * Automatically syncs track list, playback state, and other DAW state with React
 *
 * @param {React.RefObject} canvasRef - Ref to canvas element
 * @param {Object} options - AudioDAW configuration options (same as useAudioDAW)
 * @returns {Object} Object with DAW instance and synchronized state
 * @returns {AudioDAW|null} returns.daw - DAW instance
 * @returns {Array} returns.tracks - Current track list
 * @returns {boolean} returns.isPlaying - Current playback state
 * @returns {number} returns.currentTime - Current playback time
 * @returns {Object|null} returns.selection - Current selection {start, end} or null
 * @returns {number} returns.zoom - Current zoom level (pixels per second)
 * @returns {number} returns.duration - Total timeline duration in seconds
 * @returns {number} returns.scrollTime - Current horizontal scroll position in seconds
 * @returns {Array} returns.regionGroups - All region groups with their regions
 * @returns {string|null} returns.activeRegionGroupId - ID of active region group
 *
 * @example
 * function MyComponent() {
 *   const canvasRef = useRef();
 *   const { daw, tracks, isPlaying, currentTime, zoom, duration } = useAudioDAWState(canvasRef, {
 *     pixelsPerSecond: 100
 *   });
 *
 *   return (
 *     <div>
 *       <canvas ref={canvasRef} />
 *       <div>Tracks: {tracks.length}</div>
 *       <div>Status: {isPlaying ? 'Playing' : 'Stopped'}</div>
 *       <div>Time: {currentTime.toFixed(2)}s / {duration.toFixed(2)}s</div>
 *       <div>Zoom: {zoom.toFixed(1)}px/s</div>
 *     </div>
 *   );
 * }
 */
export function useAudioDAWState(canvasRef, options = {}) {
  const [tracks, setTracks] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [selection, setSelection] = useState([]);
  const [zoom, setZoom] = useState(options.pixelsPerSecond || 50);
  const [duration, setDuration] = useState(0);
  const [scrollTime, setScrollTime] = useState(0);
  const [regionGroups, setRegionGroups] = useState([]);
  const [activeRegionGroupId, setActiveRegionGroupId] = useState(null);

  // Create dawRef before callbacks so it can be used in callbacks
  const dawRef = useRef(null);

  // Merge user options with state sync callbacks
  const mergedOptions = {
    ...options,
    onTrackAdded: useCallback((trackId) => {
      setTracks(prev => {
        // Get fresh track list from DAW
        if (dawRef.current) {
          return dawRef.current.getTracks();
        }
        return prev;
      });
      options.onTrackAdded?.(trackId);
    }, [options.onTrackAdded]),

    onTrackRemoved: useCallback((trackId) => {
      setTracks(prev => {
        if (dawRef.current) {
          return dawRef.current.getTracks();
        }
        return prev;
      });
      options.onTrackRemoved?.(trackId);
    }, [options.onTrackRemoved]),

    onTrackOffsetChange: useCallback((trackId, timeOffset) => {
      setTracks(prev => {
        if (dawRef.current) {
          return dawRef.current.getTracks();
        }
        return prev;
      });
      options.onTrackOffsetChange?.(trackId, timeOffset);
    }, [options.onTrackOffsetChange]),

    onTrackVolumeChange: useCallback((trackId, volume) => {
      setTracks(prev => {
        if (dawRef.current) {
          return dawRef.current.getTracks();
        }
        return prev;
      });
      options.onTrackVolumeChange?.(trackId, volume);
    }, [options.onTrackVolumeChange]),

    onTrackPanChange: useCallback((trackId, pan) => {
      setTracks(prev => {
        if (dawRef.current) {
          return dawRef.current.getTracks();
        }
        return prev;
      });
      options.onTrackPanChange?.(trackId, pan);
    }, [options.onTrackPanChange]),

    onTrackGainChange: useCallback((trackId, gain) => {
      setTracks(prev => {
        if (dawRef.current) {
          return dawRef.current.getTracks();
        }
        return prev;
      });
      options.onTrackGainChange?.(trackId, gain);
    }, [options.onTrackGainChange]),

    onTrackCompressorChange: useCallback((trackId, enabled, settings) => {
      setTracks(prev => {
        if (dawRef.current) {
          return dawRef.current.getTracks();
        }
        return prev;
      });
      options.onTrackCompressorChange?.(trackId, enabled, settings);
    }, [options.onTrackCompressorChange]),

    onTrackColorChange: useCallback((trackId, color) => {
      setTracks(prev => {
        if (dawRef.current) {
          return dawRef.current.getTracks();
        }
        return prev;
      });
      options.onTrackColorChange?.(trackId, color);
    }, [options.onTrackColorChange]),

    onLoadingStateChange: useCallback((trackId, isLoading, loadingPhase) => {
      setTracks(prev => {
        if (dawRef.current) {
          return dawRef.current.getTracks();
        }
        return prev;
      });
      options.onLoadingStateChange?.(trackId, isLoading, loadingPhase);
    }, [options.onLoadingStateChange]),

    onPlayStateChange: useCallback((playing) => {
      setIsPlaying(playing);
      options.onPlayStateChange?.(playing);
    }, [options.onPlayStateChange]),

    onTimeUpdate: useCallback((time) => {
      setCurrentTime(time);
      options.onTimeUpdate?.(time);
    }, [options.onTimeUpdate]),

    onSelectionChange: useCallback((sel) => {
      setSelection(sel);
      options.onSelectionChange?.(sel);
    }, [options.onSelectionChange]),

    onZoomChange: useCallback((newZoom) => {
      setZoom(newZoom);
      options.onZoomChange?.(newZoom);
    }, [options.onZoomChange]),

    onDurationChange: useCallback((newDuration) => {
      setDuration(newDuration);
      options.onDurationChange?.(newDuration);
    }, [options.onDurationChange]),

    onScrollChange: useCallback((newScrollTime) => {
      setScrollTime(newScrollTime);
      options.onScrollChange?.(newScrollTime);
    }, [options.onScrollChange]),

    onRegionGroupsChange: useCallback((groups) => {
      setRegionGroups(groups);
      options.onRegionGroupsChange?.(groups);
    }, [options.onRegionGroupsChange]),

    onActiveRegionGroupChange: useCallback((groupId) => {
      setActiveRegionGroupId(groupId);
      // Update selection when active region group changes
      if (dawRef.current) {
        const activeRegions = dawRef.current.timeline.getActiveRegions();
        setSelection([...activeRegions]);
      }
      options.onActiveRegionGroupChange?.(groupId);
    }, [options.onActiveRegionGroupChange]),

    onRegionsChange: useCallback((groupId, regions) => {
      // Update region groups when regions change
      setRegionGroups(prev => {
        if (dawRef.current) {
          return dawRef.current.getRegionGroups();
        }
        return prev;
      });
      // Update selection to be the active region group's regions
      if (dawRef.current && groupId === dawRef.current.timeline.activeRegionGroupId) {
        const activeRegions = dawRef.current.timeline.getActiveRegions();
        // Create a shallow copy to ensure new reference for React to detect change
        setSelection([...activeRegions]);
      }
      options.onRegionsChange?.(groupId, regions);
    }, [options.onRegionsChange]),
  };

  const daw = useAudioDAW(canvasRef, mergedOptions);

  // Store daw in ref for callback access
  useEffect(() => {
    dawRef.current = daw;
  }, [daw]);

  // Initialize state when DAW is ready
  useEffect(() => {
    if (daw) {
      setTracks(daw.getTracks());
      setZoom(daw.getZoom());
      setDuration(daw.getDuration());
      setRegionGroups(daw.getRegionGroups());
      setActiveRegionGroupId(daw.getActiveRegionGroupId());
      // Initialize selection with active regions
      const activeRegions = daw.timeline.getActiveRegions();
      setSelection([...activeRegions]);
    }
  }, [daw]);

  return {
    daw,
    tracks,
    isPlaying,
    currentTime,
    selection,
    zoom,
    duration,
    scrollTime,
    regionGroups,
    activeRegionGroupId,
  };
}

/**
 * Hook for managing DAW state persistence
 * Provides save/load functionality with automatic state management
 *
 * @param {AudioDAW|null} daw - DAW instance from useAudioDAW
 * @returns {Object} State management functions
 * @returns {Function} returns.saveState - Save current state to object
 * @returns {Function} returns.loadState - Load state from object
 * @returns {Function} returns.exportState - Export state as JSON string
 * @returns {Function} returns.importState - Import state from JSON string
 *
 * @example
 * function MyComponent() {
 *   const canvasRef = useRef();
 *   const daw = useAudioDAW(canvasRef);
 *   const { saveState, loadState, exportState, importState } = useAudioDAWPersistence(daw);
 *   const [audioFiles, setAudioFiles] = useState({});
 *
 *   const handleSave = () => {
 *     const state = saveState();
 *     localStorage.setItem('dawState', JSON.stringify(state));
 *   };
 *
 *   const handleLoad = async () => {
 *     const state = JSON.parse(localStorage.getItem('dawState'));
 *     await loadState(state, audioFiles);
 *   };
 *
 *   return <canvas ref={canvasRef} />;
 * }
 */
export function useAudioDAWPersistence(daw) {
  const saveState = useCallback(() => {
    if (!daw) {
      throw new Error('DAW instance not available');
    }
    return daw.getState();
  }, [daw]);

  const loadState = useCallback(async (state, audioFiles = {}) => {
    if (!daw) {
      throw new Error('DAW instance not available');
    }
    await daw.setState(state, audioFiles);
  }, [daw]);

  const exportState = useCallback(() => {
    const state = saveState();
    return JSON.stringify(state, null, 2);
  }, [saveState]);

  const importState = useCallback(async (jsonString, audioFiles = {}) => {
    const state = JSON.parse(jsonString);
    await loadState(state, audioFiles);
  }, [loadState]);

  return {
    saveState,
    loadState,
    exportState,
    importState,
  };
}

/**
 * Hook for managing audio file references for state restoration
 * Maintains a map of track IDs to audio files for use with setState
 *
 * @returns {Object} File management functions
 * @returns {Object} returns.audioFiles - Map of track ID to File/ArrayBuffer
 * @returns {Function} returns.registerAudioFile - Register audio file for a track
 * @returns {Function} returns.unregisterAudioFile - Remove audio file reference
 * @returns {Function} returns.clearAudioFiles - Clear all audio file references
 *
 * @example
 * function MyComponent() {
 *   const canvasRef = useRef();
 *   const daw = useAudioDAW(canvasRef);
 *   const { audioFiles, registerAudioFile } = useAudioFileRegistry();
 *
 *   const handleAddTrack = async (file) => {
 *     const trackId = await daw.addTrack(file, { fileName: file.name });
 *     registerAudioFile(trackId, file); // Keep reference for later restoration
 *   };
 *
 *   return <canvas ref={canvasRef} />;
 * }
 */
export function useAudioFileRegistry() {
  const [audioFiles, setAudioFiles] = useState({});

  const registerAudioFile = useCallback((trackId, audioFile) => {
    setAudioFiles(prev => ({
      ...prev,
      [trackId]: audioFile
    }));
  }, []);

  const unregisterAudioFile = useCallback((trackId) => {
    setAudioFiles(prev => {
      const { [trackId]: removed, ...rest } = prev;
      return rest;
    });
  }, []);

  const clearAudioFiles = useCallback(() => {
    setAudioFiles({});
  }, []);

  return {
    audioFiles,
    registerAudioFile,
    unregisterAudioFile,
    clearAudioFiles,
  };
}
